

import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";

interface PrintableInvoiceProps {
  invoice: any;
  businessInfo: {
    name: string;
    logoUrl?: string;
    tagline?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstNumber?: string;
  };
}

export default function PrintableInvoice({ invoice, businessInfo }: PrintableInvoiceProps) {
  const subtotal = invoice.subtotal || invoice.total_amount || 0;
  const total = invoice.total_amount || 0;
  const paid = invoice.paid_amount || 0;
  const balance = invoice.balance_due || (total - paid);
  const items = invoice.items || [];
  const clientName = invoice.client?.name || "—";
  const eventName = invoice.event?.name || invoice.event_name || "—";

  return (
    <div id="print-invoice-container" className="print-container hidden print:block">
      <div className="print-page">
        {/* Header with Gold Accent */}
        <div className="print-header-accent" />
        
        {/* Business Header — Option A: Logo · Name+Tagline · Number box */}
        <div className="print-header">
          <div style={{ display: "flex", alignItems: "center", gap: "5mm", marginBottom: "3mm" }}>
            {/* Logo */}
            {businessInfo.logoUrl && (
              <div style={{ flexShrink: 0 }}>
                <img
                  src={getImageUrl(businessInfo.logoUrl)}
                  alt="Logo"
                  className="print-logo"
                  style={{ height: "55px", maxWidth: "130px", objectFit: "contain", display: "block" }}
                />
              </div>
            )}

            {/* Business name + tagline — centered */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <p style={{ fontSize: "18pt", fontWeight: 800, color: "#111827", letterSpacing: "-0.3px", lineHeight: 1.1, marginBottom: "1.5mm" }}>
                {businessInfo.name.toUpperCase()}
              </p>
              <p style={{ fontSize: "10pt", color: "#C9A84C", fontWeight: 600 }}>
                {businessInfo.tagline || "BANQUET HALL & EVENT SERVICES"}
              </p>
            </div>

            {/* Invoice number box */}
            <div className="print-invoice-number" style={{ flexShrink: 0, padding: "1.5mm 2.5mm", textAlign: "right" }}>
              <p style={{ fontSize: "6pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#6b7280", marginBottom: "0.5mm" }}>
                Invoice No.
              </p>
              <p style={{ fontSize: "11pt", fontWeight: 800, color: "#C9A84C", lineHeight: 1.1 }}>
                {invoice.invoice_number || `INV-${String(invoice.id).padStart(4, "0")}`}
              </p>
              <p style={{ fontSize: "6.5pt", color: "#6b7280", marginTop: "0.5mm" }}>
                <span style={{ fontWeight: 600 }}>Date:</span> {formatDate(invoice.invoice_date)}
                {invoice.due_date && <span>{"  •  "}<span style={{ fontWeight: 600 }}>Due:</span> {formatDate(invoice.due_date)}</span>}
              </p>
            </div>
          </div>

          {/* Contact info — address line, then contact details line */}
          <div style={{ textAlign: "center", fontSize: "8pt", color: "#6b7280", lineHeight: 1.6, paddingBottom: "2mm" }}>
            {businessInfo.address && (
              <p>{businessInfo.address}</p>
            )}
            <p>
              {[
                businessInfo.phone ? `Phone: ${businessInfo.phone}` : null,
                businessInfo.email ? `Email: ${businessInfo.email}` : null,
                businessInfo.gstNumber ? `GSTIN: ${businessInfo.gstNumber}` : null,
              ].filter(Boolean).join("  •  ")}
            </p>
          </div>
        </div>

        <div className="print-divider" />

        {/* Client & Event Info Cards — inline flex for reliable print layout */}
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", marginBottom: "4mm", gap: "4mm" }}>
          {[
            { label: "Client", value: clientName },
            { label: "Event", value: eventName },
            { label: "Invoice Date", value: formatDate(invoice.invoice_date) },
            { label: "Status", value: invoice.status, amber: true },
          ].map((card, i) => (
            <div
              key={i}
              style={{
                flex: "1 1 0",
                textAlign: "center",
                padding: "2mm 1mm",
                borderLeft: i > 0 ? "1px solid #e5e7eb" : "none",
              }}
            >
              <p className="print-info-card-label">{card.label}</p>
              <p className="print-info-card-value" style={card.amber ? { color: "#C9A84C" } : {}}>
                {card.value}
              </p>
            </div>
          ))}
        </div>

        {/* Items Table */}
        {items.length > 0 && (
          <div className="mb-4">
            <div className="bg-stone-100 px-3 py-2 mb-2 border-l-4 border-amber-500">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider">Items</h3>
            </div>
            <table className="print-table">
              <thead>
                <tr>
                  <th className="text-left">Description</th>
                  <th className="text-center w-16">Type</th>
                  <th className="text-center w-16">QTY</th>
                  <th className="text-right w-28">Rate</th>
                  <th className="text-right w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item: any, idx: number) => {
                  const itemPrice = item.unit_price || item.price || 0;
                  return (
                    <tr key={`item-${idx}`}>
                      <td className="font-medium text-stone-800">{item.name || item.description || "Item"}</td>
                      <td className="text-center text-xs text-stone-500 uppercase">{item.type || "—"}</td>
                      <td className="text-center text-stone-700">{item.quantity || 1}</td>
                      <td className="text-right text-stone-600">
                        {itemPrice > 0 ? formatCurrencyFull(itemPrice) : '—'}
                      </td>
                      <td className="text-right font-bold text-stone-900">
                        {formatCurrencyFull((item.quantity || 1) * itemPrice)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Table */}
        <div className="mb-4 mt-4">
          <table className="print-table print-summary-table">
            <tbody>
              {invoice.discount_amount > 0 && (
                <tr>
                  <td colSpan={4} className="text-right text-stone-600">Discount</td>
                  <td className="text-right w-28 text-red-600">-{formatCurrencyFull(invoice.discount_amount)}</td>
                </tr>
              )}
              <tr className="border-t-2 border-stone-300">
                <td colSpan={4} className="text-right font-semibold text-stone-700">SUBTOTAL</td>
                <td className="text-right font-bold w-28 text-stone-900">{formatCurrencyFull(subtotal)}</td>
              </tr>
              {invoice.tax_amount > 0 && (
                <tr>
                  <td colSpan={4} className="text-right text-stone-600">Tax</td>
                  <td className="text-right w-28 text-stone-600">{formatCurrencyFull(invoice.tax_amount)}</td>
                </tr>
              )}
              <tr className="print-total-row">
                <td colSpan={4} className="text-right font-bold text-lg">TOTAL AMOUNT</td>
                <td className="text-right font-bold w-28 text-lg">{formatCurrencyFull(total)}</td>
              </tr>
              <tr>
                <td colSpan={4} className="text-right text-stone-600">Amount Paid</td>
                <td className="text-right w-28 text-emerald-600 font-bold">
                  {formatCurrencyFull(paid)}
                </td>
              </tr>
              <tr className={balance > 0 ? "print-balance-due" : "print-balance-paid"}>
                <td colSpan={4} className="text-right font-bold text-base">Balance Due</td>
                <td className="text-right font-bold w-28 text-base">{formatCurrencyFull(balance)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Payment Status Banner */}
        <div className="print-payment-status mb-8">
          <div>
            <p className="font-bold text-sm mb-1.5 uppercase tracking-wide">Payment Status</p>
            <p className="text-xs text-stone-700">
              <span className="font-semibold">Paid:</span> {formatCurrencyFull(paid)} <span className="text-stone-400">of</span> {formatCurrencyFull(total)}
            </p>
          </div>
          <div className="text-right">
            {balance > 0 ? (
              <p className="text-red-600 font-bold text-base">
                Outstanding: {formatCurrencyFull(balance)}
              </p>
            ) : (
              <p className="text-emerald-600 font-bold text-base">✓ FULLY PAID</p>
            )}
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="mb-8">
          <h3 className="text-sm font-bold text-stone-900 mb-3 uppercase tracking-wide border-b border-stone-300 pb-2">Terms & Conditions</h3>
          <ul className="text-xs text-stone-700 space-y-1.5 leading-relaxed">
            <li>• <span className="font-semibold">Payment Terms:</span> 50% advance required to confirm booking.</li>
            <li>• <span className="font-semibold">Balance Due:</span> 7 days before the event date.</li>
            <li>• <span className="font-semibold">Cancellation Policy:</span> Cancellations made 30+ days before event are refundable.</li>
            <li>• This invoice is electronically generated and valid without signature.</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <div>
            <p className="font-bold text-sm text-stone-900">Thank you for choosing {businessInfo.name}!</p>
            <p className="text-xs text-stone-600 mt-1.5">
              <span className="font-semibold">For queries:</span> {businessInfo.email || "info@shalimar.com"} | {businessInfo.phone || "+91-XXXX-XXXX"}
            </p>
          </div>
          <div className="text-right text-xs text-stone-500">
            <p className="font-semibold text-amber-600">Generated on</p>
            <p className="mt-0.5">{new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
